import { useState } from "react";
import { Plus } from "lucide-react";

import {
  PoliciesService,
  type PolicyRuleResponse,
} from "@/api";
import { useApi } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/shared/page-header";
import { PoliciesList } from "@/components/policies/policies-list";
import { PoliciesQuickstart } from "@/components/policies/policies-quickstart";
import { PolicyCreateDialog } from "@/components/policies/policy-create-dialog";
import { PolicyDeleteDialog } from "@/components/policies/policy-delete-dialog";

export default function PoliciesPage() {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PolicyRuleResponse | null>(
    null
  );

  const { data, error, isLoading, refetch } = useApi(
    () => PoliciesService.list({ limit: 200, include_disabled: true })
  );

  const items = data?.items ?? [];
  const hasNoPolicies = !isLoading && items.length === 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("policies.page.title")}
        description={t("policies.page.description")}
        action={
          <Button onClick={() => setCreateOpen(true)} size="sm">
            <Plus size={16} />
            {t("policies.actions.create")}
          </Button>
        }
      />

      {error && <Alert variant="danger">{parseApiError(error)}</Alert>}

      {hasNoPolicies ? (
        <PoliciesQuickstart onCreateClick={() => setCreateOpen(true)} />
      ) : (
        <PoliciesList
          items={items}
          isLoading={isLoading}
          onDelete={setDeleteTarget}
        />
      )}

      <PolicyCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => {
          setCreateOpen(false);
          void refetch();
        }}
      />

      <PolicyDeleteDialog
        rule={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onSuccess={() => {
          setDeleteTarget(null);
          void refetch();
        }}
      />
    </div>
  );
}
